/**
 * LeadAutomationCard — qué está haciendo la automatización con este lead.
 *
 * Compacto a propósito. La ficha del lead es el sitio de trabajo de quien
 * vende, y lo primero sigue siendo la conversación y la próxima acción: esto
 * es contexto, no un panel de control.
 *
 * Lo que NO es: la próxima acción del comercial. Un paso automático no es una
 * tarea de nadie y nunca se enseña como tal — por eso la tarjeta dice
 * "Siguiente automático" y no "Próxima acción", y vive en su propia caja.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Clock, PauseCircle, PlayCircle, Prohibit, Robot, WarningCircle, Check,
  ShieldCheck, HandPalm,
} from "@phosphor-icons/react";
import { formatError } from "@/lib/api";
import {
  AUTOMATION_STATUS_LABEL, AUTOMATION_STATUS_TONE, automationControlsFor,
  automationReasonText, controlLeadAutomation, controlLeadPermission,
  getLeadAutomation, optOutLabel, permissionActionFor,
} from "@/lib/automationApi";

const TAP = "min-h-[44px]";

const TONE_CLASS = {
  blue: "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-800",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800",
  amber: "bg-amber-50 text-amber-900 border-amber-300 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800",
  red: "bg-red-50 text-red-800 border-red-300 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800",
  stone: "bg-stone-100 text-stone-700 border-stone-300 dark:bg-stone-800 dark:text-stone-300 dark:border-stone-700",
};

const CONTROL_META = {
  pause: { label: "Pausar", Icon: PauseCircle },
  resume: { label: "Reanudar", Icon: PlayCircle },
  cancel: { label: "Cancelar", Icon: Prohibit },
};

/** "mañana 10:00" / "hoy 18:30" / "el 16 sept 09:00". */
function whenLabel(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  const hhmm = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((day - today) / 86400000);
  if (diff === 0) return `hoy ${hhmm}`;
  if (diff === 1) return `mañana ${hhmm}`;
  if (diff === -1) return `ayer ${hhmm}`;
  return `${d.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} ${hhmm}`;
}

/** "12 sept 2026". */
function dayLabel(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return new Date(t).toLocaleDateString("es-ES",
    { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Solo enseña lo que sigue teniendo efecto operativo: la exclusión/baja.
 *
 * El antiguo registro de consentimiento se conserva en backend para
 * auditoría, pero desde #213 ya no autoriza ni bloquea automatizaciones.
 * Mostrar "Sin consentimiento registrado" aquí hacía parecer que había un
 * problema cuando el motor lo ignora por diseño.
 */
function PermissionRow({ permission, isAdmin, busy, onAct }) {
  if (!permission) return null;
  const action = permissionActionFor(permission);
  const outDay = dayLabel(permission.opted_out_at);

  if (!permission.opted_out) {
    if (!isAdmin || action !== "opt_out") return null;
    return (
      <button
        type="button"
        onClick={() => onAct("opt_out")}
        disabled={Boolean(busy)}
        data-testid="lead-permission-opt-out"
        className={`${TAP} inline-flex items-center gap-1.5 px-3 rounded-md border border-stone-300 dark:border-stone-700 text-xs text-stone-700 dark:text-stone-300 hover:border-red-400 hover:text-red-700 disabled:opacity-50`}
      >
        <HandPalm size={14} />
        {busy === "opt_out" ? "…" : "Excluir de automatización"}
      </button>
    );
  }

  return (
    <div className="rounded-md border border-red-200 dark:border-red-900 px-2.5 py-2 space-y-1.5"
         data-testid="lead-automation-permission">
      <div className="flex items-center gap-1.5 text-xs"
           data-testid="lead-permission-optout">
        <HandPalm size={13} weight="fill" className="text-red-600 shrink-0" />
        <span className="text-red-700 dark:text-red-300">
          {optOutLabel(permission) || "Dado de baja"}
        </span>
        {outDay && <span className="text-stone-400">· {outDay}</span>}
      </div>

      {isAdmin && action === "allow" && (
        <button
          type="button"
          onClick={() => onAct("allow")}
          disabled={Boolean(busy)}
          data-testid="lead-permission-allow"
          className={`${TAP} inline-flex items-center gap-1.5 px-3 rounded-md border border-stone-300 dark:border-stone-700 text-xs text-stone-700 dark:text-stone-300 hover:border-emerald-500 hover:text-emerald-700 disabled:opacity-50`}
        >
          <ShieldCheck size={14} />
          {busy === "allow" ? "…" : "Volver a permitir"}
        </button>
      )}
    </div>
  );
}

export default function LeadAutomationCard({ leadId, isAdmin = false }) {
  const [state, setState] = useState({
    loading: true, enabled: false, sequence: null, blockedReason: null,
    permission: null,
  });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await getLeadAutomation(leadId);
      setState({ loading: false, ...data });
      setError("");
    } catch (e) {
      // La automatización es contexto: si no se puede leer, la ficha del lead
      // sigue siendo perfectamente usable. Se dice y se calla.
      setState({ loading: false, enabled: false, sequence: null,
                 blockedReason: null, permission: null });
      setError(formatError(e));
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    setBusy(action);
    try {
      const sequence = await controlLeadAutomation(leadId, action);
      setState((s) => ({ ...s, sequence }));
      setError("");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(null);
    }
  };

  const actOnPermission = async (action) => {
    setBusy(action);
    try {
      const { permission, sequence } = await controlLeadPermission(leadId, action);
      setState((s) => ({ ...s, permission, sequence }));
      setError("");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(null);
    }
  };

  if (state.loading) return null;

  const permissionBlock = (
    <PermissionRow
      permission={state.permission}
      isAdmin={isAdmin}
      busy={busy}
      onAct={actOnPermission}
    />
  );

  const seq = state.sequence;
  if (!seq) {
    // Sin secuencia no se inventa una tarjeta vacía. La única excepción
    // útil es el control de exclusión para admin (o una baja ya existente).
    const why = automationReasonText(state.blockedReason);
    const hasPermissionUi = Boolean(
      state.permission && (state.permission.opted_out || isAdmin)
    );
    if (!state.enabled && !hasPermissionUi) return null;
    return (
      <div className="dv-card text-xs text-stone-500 dark:text-stone-400 space-y-2"
           data-testid="lead-automation-none">
        {state.enabled && (
          <div>
            <Robot size={14} className="inline mr-1.5 -mt-0.5" />
            Este lead no está en ninguna secuencia automática.
            {why && (
              <span className="block mt-1 text-stone-600 dark:text-stone-300"
                    data-testid="lead-automation-blocked">
                {why}
              </span>
            )}
          </div>
        )}
        {permissionBlock}
        {error && (
          <div className="text-xs text-red-700 dark:text-red-300"
               data-testid="lead-automation-error">
            {error}
          </div>
        )}
      </div>
    );
  }

  const tone = AUTOMATION_STATUS_TONE[seq.status] || "stone";
  const controls = isAdmin ? automationControlsFor(seq.status) : [];
  const reason = automationReasonText(seq.cancel_reason);
  const next = whenLabel(seq.next_run_at);
  const done = seq.last_successful_step || 0;

  return (
    <div className="dv-card space-y-3" data-testid="lead-automation">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Robot size={16} className="shrink-0 text-stone-400" />
          <span className="text-sm text-stone-900 dark:text-stone-100">
            Secuencia automática
          </span>
          <span
            className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${TONE_CLASS[tone]}`}
            data-testid="lead-automation-status"
          >
            {AUTOMATION_STATUS_LABEL[seq.status] || seq.status}
          </span>
        </div>
        <span className="text-xs text-stone-400 tabular-nums" data-testid="lead-automation-progress">
          {done}/{seq.total_steps}
        </span>
      </div>

      {/* Los pasos, con lo único que se sabe de cada uno: si salió y cuándo. */}
      <ol className="space-y-1" data-testid="lead-automation-steps">
        {(seq.steps || []).map((s) => (
          <li key={s.step} className="flex items-center gap-2 text-xs"
              data-testid={`lead-automation-step-${s.step}`}>
            {s.state === "sent"
              ? <Check size={12} weight="bold" className="text-emerald-600 shrink-0" />
              : <span className="w-3 h-3 rounded-full border border-stone-300 dark:border-stone-600 shrink-0" />}
            <span className={s.state === "sent"
              ? "text-stone-700 dark:text-stone-300"
              : "text-stone-400"}>
              {s.label}
            </span>
            {s.at && (
              <span className="text-stone-400">· {whenLabel(s.at)}</span>
            )}
          </li>
        ))}
      </ol>

      {/* Siguiente contacto automático. NO es la próxima acción del comercial:
          esa vive en su propia sección y la decide una persona. */}
      {next && (seq.status === "scheduled" || seq.status === "active") && (
        <div className="flex items-center gap-1.5 text-xs text-stone-600 dark:text-stone-300"
             data-testid="lead-automation-next">
          <Clock size={12} />
          Siguiente automático: {next}
        </div>
      )}

      {reason && (
        <div className={`text-xs rounded-md border px-2.5 py-2 ${TONE_CLASS[tone]}`}
             data-testid="lead-automation-reason">
          {seq.status === "failed" && (
            <WarningCircle size={12} className="inline mr-1 -mt-0.5" />
          )}
          {reason}
          {seq.paused_by && seq.status === "paused" && ` · ${seq.paused_by}`}
        </div>
      )}

      {permissionBlock}

      {controls.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1" data-testid="lead-automation-controls">
          {controls.map((key) => {
            const { label, Icon } = CONTROL_META[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => act(key)}
                disabled={Boolean(busy)}
                data-testid={`lead-automation-${key}`}
                className={`${TAP} inline-flex items-center gap-1.5 px-3 rounded-md border border-stone-300 dark:border-stone-700 text-xs text-stone-700 dark:text-stone-300 hover:border-stone-500 disabled:opacity-50`}
              >
                <Icon size={14} />
                {busy === key ? "…" : label}
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="text-xs text-red-700 dark:text-red-300" data-testid="lead-automation-error">
          {error}
        </div>
      )}
    </div>
  );
}
